package dev.youndie.site.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initKafkaknPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("kafkakn.md", FrontMatterElement.Builder { addScalar("title", "kafkakn"); addScalar("searchTitle", "Kafka client for Kotlin Multiplatform — librdkafka on Kotlin/Native, the JVM client as its oracle"); addScalar("description", "A Kafka client for Kotlin Multiplatform: librdkafka linked statically inside the klib on Kotlin/Native, the official Java client on the JVM as the oracle. One test suite runs on both arms against one broker."); addScalar("library", "kafkakn"); }))
}

@Page("/kafkakn")
@Layout(".components.layouts.LibraryLayout")
@Composable
fun KafkaknPage() {
    org.jetbrains.compose.web.dom.H2(attrs = { id("why-it-exists") }) {
        org.jetbrains.compose.web.dom.Text("Why it exists")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A service compiled to a single Kotlin/Native binary has no way to talk to Kafka: the clients are JVM libraries, and the one native client ever published for Kotlin (")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("com.icemachined:kafka-client")
        }
        org.jetbrains.compose.web.dom.Text(" 0.2.0, October 2022) ships three native artefacts and ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("no JVM variant")
        }
        org.jetbrains.compose.web.dom.Text(" — so it has no way to check itself except by believing what a broker tells it. ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/kafkakn") {
            org.jetbrains.compose.web.dom.Text("kafkakn")
        }
        org.jetbrains.compose.web.dom.Text(" is built the other way round. The ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("jvm")
        }
        org.jetbrains.compose.web.dom.Text(" target is not there for portability; it is there as the ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("differential oracle")
        }
        org.jetbrains.compose.web.dom.Text(": one ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("commonTest")
        }
        org.jetbrains.compose.web.dom.Text(" suite runs on both arms against one broker, and the native arm is correct when it agrees with the reference implementation.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That asymmetry has already paid twice. The two clients put the same key on different partitions — librdkafka hashes with CRC32, the Java producer with murmur2 — and each arm was consistent with the broker on its own, so neither could have noticed alone. And the first published klib carried the bindings and not the C: this repository's own binaries linked because of options on the build machine, and a stranger's link failed with fourteen undefined symbols. It took a build that was not this one to find it.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-you-get") }) {
        org.jetbrains.compose.web.dom.Text("What you get")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("A producer whose ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("send")
                }
                org.jetbrains.compose.web.dom.Text(" suspends until the broker has acknowledged.")
            }
            org.jetbrains.compose.web.dom.Text(" Holding a ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("RecordMetadata")
            }
            org.jetbrains.compose.web.dom.Text(" ")
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("is")
            }
            org.jetbrains.compose.web.dom.Text(" the acknowledgement; there is no third outcome to inspect. This follows from one measured fact: ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("rd_kafka_produce")
            }
            org.jetbrains.compose.web.dom.Text(" only enqueues, a record that was never queued produces ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("no delivery report")
            }
            org.jetbrains.compose.web.dom.Text(", and a binding that counts delivery reports can lose 264 826 records of a million while flushing successfully. No public API here exposes a delivery-report count, and that is a build gate rather than a habit.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("A consumer")
            }
            org.jetbrains.compose.web.dom.Text(" that assigns and seeks, joins groups with manual commits, offers a ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("Flow")
            }
            org.jetbrains.compose.web.dom.Text(" over ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("poll")
            }
            org.jetbrains.compose.web.dom.Text(", cooperative rebalancing, static membership, and the KIP-848 group protocol as a value the caller chooses.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("An admin client")
            }
            org.jetbrains.compose.web.dom.Text(": create, delete and describe topics; describe the cluster; list and describe consumer groups and read their committed offsets and lag; move and delete group offsets; change a topic's configuration incrementally; add partitions; delete records.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("TLS, client certificates, SASL PLAIN, SCRAM and OAUTHBEARER")
            }
            org.jetbrains.compose.web.dom.Text(" with a token the caller supplies. Certificate trust cannot be turned off: the key that would do it is refused on both arms, because it exists only on the one without an oracle.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("librdkafka and its TLS stack inside the klib.")
            }
            org.jetbrains.compose.web.dom.Text(" A downstream link needs no configuration of its own, and ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("ldd")
            }
            org.jetbrains.compose.web.dom.Text(" on the linked binary is the same set as on the same binary built without kafkakn — measured on a downstream build, not on this repository's test binary.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("install") }) {
        org.jetbrains.compose.web.dom.Text("Install")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Published to reposilite only, never to Maven Central, with no compatibility promise. Every publish is a version of its own, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("0.1.0.<n>")
        }
        org.jetbrains.compose.web.dom.Text(", tagged ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("v0.1.0.<n>")
        }
        org.jetbrains.compose.web.dom.Text(" at the commit it was built from and never overwritten; the newest is the highest tag.")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""plugins {
    kotlin("multiplatform") version "2.4.20"
}

repositories {
    maven("https://reposilite.kotlin.website/snapshots") {
        content { includeGroupAndSubgroups("io.github.youndie") }
    }
    mavenCentral()
}

kotlin {
    linuxX64("native") {
        binaries.executable { entryPoint = "main" }
    }
    sourceSets.commonMain.dependencies {
        implementation("io.github.youndie.kafkakn:kafkakn-core:0.1.0.12")
    }
}
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The Kotlin version is part of the instructions.")
        }
        org.jetbrains.compose.web.dom.Text(" A klib carries metadata that a build on another compiler refuses, so ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("2.4.20")
        }
        org.jetbrains.compose.web.dom.Text(" is the version this is known to work with rather than a placeholder. Measured from an empty machine — a JDK, Gradle and nothing else — that build file gets to a record on a topic in 106–109 s, of which 74–78 s is the Kotlin/Native toolchain downloading.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-the-native-artefact-requires") }) {
        org.jetbrains.compose.web.dom.Text("What the native artefact requires")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The same shared libraries as a binary built without it: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("libc")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("libgcc_s")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("libm")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("libpthread")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("libresolv")
        }
        org.jetbrains.compose.web.dom.Text(" and the rest of the loader's usual set — linking kafkakn adds none and removes none. The one thing it does raise is the ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("glibc floor, to 2.17")
        }
        org.jetbrains.compose.web.dom.Text(", which is ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("manylinux2014")
        }
        org.jetbrains.compose.web.dom.Text("'s, because that is where the C bundle is built. The bundle carries a one-line local patch to librdkafka 2.13.0 for that glibc, kept as a file in the repository and re-applied on every bump; nothing from this project is filed upstream.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("limitations") }) {
        org.jetbrains.compose.web.dom.Text("Limitations")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("jvm")
            }
            org.jetbrains.compose.web.dom.Text(" and ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("linuxX64")
            }
            org.jetbrains.compose.web.dom.Text(" are the published targets. ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("linuxArm64")
            }
            org.jetbrains.compose.web.dom.Text(" and ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("macosArm64")
            }
            org.jetbrains.compose.web.dom.Text(" build for contributors and pass the same suite against the same broker, but are ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("not published and not run in CI")
            }
            org.jetbrains.compose.web.dom.Text(", by decision. No ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("macosX64")
            }
            org.jetbrains.compose.web.dom.Text(", no Windows, no ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("musl")
            }
            org.jetbrains.compose.web.dom.Text(".")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Reposilite only; nothing on Maven Central, and no compatibility promise between versions. What would change that is a condition, not a date: somebody outside this portfolio wanting the library.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Topic metadata is planned and not yet in. OIDC token fetching is not planned — the native bundle has no curl, so OAUTHBEARER takes a token the caller already has.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Schema Registry and Streams are in neither client underneath, so they are not a gap between kafkakn and what it wraps — and they are not here either.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("send")
            }
            org.jetbrains.compose.web.dom.Text(" does not batch for you: one call is one record and one acknowledgement. Throughput comes from calling it concurrently; both clients batch internally once records are in flight together.")
        }
    }
}
